import React, { cloneElement } from 'react';
import { motion } from 'framer-motion';
import { Stethoscope, Heart, ArrowUpRight, ShieldCheck, Clock, Users, MapPin } from 'lucide-react';
export function ServicesSection() {
  const services = [{
    title: 'Skilled Care',
    description: 'Professional medical services provided by licensed nurses and therapists in the comfort of your home.',
    image: 'https://www.harmonyhealthcaremd.com/wp-content/themes/harmonyhealthcarees376/images/middle-box1-img.jpg',
    icon: <Stethoscope className="w-6 h-6" data-id="element-242" />,
    color: 'bg-primary',
    features: ['Wound Care', 'IV Therapy', 'Physical Therapy', 'Medication Management']
  }, {
    title: 'Personal Care',
    description: 'Dedicated assistance with daily living activities to help you maintain independence and dignity.',
    image: 'https://www.harmonyhealthcaremd.com/wp-content/themes/harmonyhealthcarees376/images/middle-box2-img.jpg',
    icon: <Heart className="w-6 h-6" data-id="element-243" />,
    color: 'bg-secondary',
    features: ['Bathing & Grooming', 'Meal Preparation', 'Mobility Support', 'Companionship']
  }];
  return <section id="services" className="py-24 bg-background-secondary relative overflow-hidden" data-id="element-244">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12 translate-x-1/2" data-id="element-245" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-246">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-247">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-248">
            Excellence in Care
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-4xl lg:text-5xl font-heading font-bold text-dark-text mb-6" data-id="element-249">
            Services We Offer
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-lg text-body-text" data-id="element-250">
            We provide a comprehensive range of healthcare services tailored to
            meet the unique needs of every individual we serve.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12" data-id="element-251">
          {services.map((service, index) => <motion.div key={service.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.2
        }} className="group relative bg-white rounded-[40px] overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100" data-id="element-252">
              <div className="grid md:grid-cols-2" data-id="element-253">
                <div className="relative h-64 md:h-full overflow-hidden" data-id="element-254">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-255" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-256" />
                </div>

                <div className="p-10 flex flex-col justify-between" data-id="element-257">
                  <div data-id="element-258">
                    <div className={`w-14 h-14 ${service.color} rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-current/20`} data-id="element-259">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-dark-text mb-4" data-id="element-260">
                      {service.title}
                    </h3>
                    <p className="text-body-text mb-8 leading-relaxed" data-id="element-261">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-10" data-id="element-262">
                      {service.features.map(feature => <li key={feature} className="flex items-center gap-2 text-sm font-medium text-dark-text" data-id="element-263">
                          <div className={`w-1.5 h-1.5 rounded-full ${service.color}`} data-id="element-264" />
                          {feature}
                        </li>)}
                    </ul>
                  </div>

                  <button className="inline-flex items-center gap-2 text-primary font-bold group/btn" data-id="element-265">
                    Learn More
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center transition-all duration-300 group-hover/btn:bg-primary group-hover/btn:text-white" data-id="element-266">
                      <ArrowUpRight className="w-4 h-4" data-id="element-267" />
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Trust Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-24" data-id="element-268">
          {[{
          icon: <Users data-id="element-269" />,
          label: '1000+',
          sub: 'Families Supported'
        }, {
          icon: <Clock data-id="element-270" />,
          label: '24/7',
          sub: 'Availability'
        }, {
          icon: <ShieldCheck data-id="element-271" />,
          label: 'Licensed',
          sub: 'Healthcare Professionals'
        }, {
          icon: <MapPin data-id="element-272" />,
          label: 'Maryland',
          sub: 'Wide Coverage'
        }].map((metric, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="p-8 bg-white rounded-3xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow" data-id="element-273">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mx-auto mb-4" data-id="element-274">
                {cloneElement(metric.icon as React.ReactElement, {
              className: 'w-6 h-6'
            })}
              </div>
              <div className="text-2xl font-bold text-dark-text mb-1" data-id="element-275">
                {metric.label}
              </div>
              <div className="text-sm text-body-text font-medium" data-id="element-276">
                {metric.sub}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}